import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Card } from '@/components/ui/card';
import { UnityLogo } from '@/components/shared/unity-logo';
import { cn } from '@/utils/cn';

import './auth.css';

/** Same floor as Tailwind `sm` — the QR rail is a side-by-side desktop affordance. */
const QR_RAIL_MIN_WIDTH = '(min-width: 640px)';

const useCanShowQrRail = () => {
  const [canShow, setCanShow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(QR_RAIL_MIN_WIDTH).matches : false,
  );

  useEffect(() => {
    const media = window.matchMedia(QR_RAIL_MIN_WIDTH);
    const sync = () => setCanShow(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return canShow;
};

/** Brand header, compact split card, and footer links for the sign-in page. */
export const SignInShell = ({
  form,
  qr,
  showQr = true,
  footer,
}: {
  form: ReactNode;
  qr?: ReactNode;
  /** Hide the QR rail during 2FA or other full-width steps. */
  showQr?: boolean;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const canShowQrRail = useCanShowQrRail();
  const showQrRail = showQr && Boolean(qr) && canShowQrRail;

  return (
    <div className="auth-brand-shell flex min-h-dvh flex-col items-center justify-center gap-6 px-4 py-8 text-on-brand sm:py-10">
      <div className="auth-brand-shell-content flex w-full flex-col items-center gap-6">
      <div className="text-center">
        <Link
          to="/"
          className="inline-flex flex-col items-center gap-3 text-center text-on-brand transition-colors hover:text-on-brand/90"
        >
          <UnityLogo
            variant="horizontal-on-dark"
            className="h-10 w-auto"
            label={t('app.name', { ns: 'common' })}
          />
          <p className="text-body-sm text-on-brand/85">{t('brand.tagline')}</p>
        </Link>
      </div>

      <Card
        className={cn(
          'overflow-hidden text-fg shadow-e3',
          showQrRail ? 'w-full max-w-[36rem] sm:w-fit sm:max-w-none' : 'w-full max-w-lg',
        )}
      >
        <div className={cn('flex flex-col', showQrRail && 'sm:flex-row sm:items-stretch')}>
          <div className={cn('p-5 sm:p-8', showQrRail ? 'sm:w-[23rem]' : 'w-full')}>{form}</div>
          {showQrRail ? (
            <>
              <div className="hidden w-px shrink-0 bg-border sm:block" aria-hidden />
              <div className="hidden w-full flex-col items-center justify-center p-6 text-center sm:flex sm:w-[14rem] sm:shrink-0">
                {qr}
              </div>
            </>
          ) : null}
        </div>
      </Card>

      {footer ?? (
        <div className="text-body-sm text-on-brand/85">
          <div className="flex flex-col items-center gap-2 text-center">
            <p>
              {t('signIn.noAccount')}{' '}
              <Link to="/register" className="font-medium text-on-brand hover:text-on-brand/90">
                {t('signIn.createAccount')}
              </Link>
            </p>
            <Link to="/" className="text-on-brand hover:text-on-brand/90">
              {t('backToHome')}
            </Link>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
